// Guards against Formie submissions being sent with blanked hidden inputs.
//
// Safari (iOS and macOS) can suspend a tab and later restore it, re-applying
// saved form state to the rebuilt page. That restore wipes the server-rendered
// hidden input values (action, handle, CSRF token etc) while keeping the
// user's typed fields, so the submission POSTs an empty action and Craft
// responds 404 (DEV-13033).
//
// The restore can happen at any point, including mid-fill when the user
// switches apps and comes back, so one-off checks aren't enough. Repair
// whenever the page is (re)shown or refocused, and again at the moment of
// submission via a capture-phase click handler, which runs before Formie's
// own submit pipeline regardless of how it intercepts the form.

const FORMIE_ACTION = 'formie/submissions/submit'

function csrfTokenName() {
	return window.SERVD_CSRF_TOKEN_NAME || 'CRAFT_CSRF_TOKEN'
}

function repairForm($form, reason) {
	if (!$form) {
		return
	}

	let repaired = []

	const fix = (name, value) => {
		const $input = $form.querySelector(`input[name="${name}"]`)
		if ($input && !$input.value && value) {
			$input.value = value
			repaired.push(name)
		}
	}

	fix('action', FORMIE_ACTION)
	fix('handle', $form.getAttribute('data-form-handle'))
	fix('submitAction', 'submit')

	if (repaired.length) {
		console.warn(`[formie-guard] repaired blanked hidden inputs (${reason}):`, repaired.join(', '))
	}

	// CSRF is session-bound so we can't restore it inline; ask Servd's
	// injector (present on statically cached pages) to re-fetch it. Doing
	// this at restore time gives it time to land before the user submits.
	const $csrf = $form.querySelector(`input[name="${csrfTokenName()}"]`)
	if ($csrf && !$csrf.value && typeof window.injectCSRF === 'function') {
		window.injectCSRF()
	}
}

function repairAll(reason) {
	document.querySelectorAll('form[data-form-handle]').forEach(($form) => repairForm($form, reason))
}

// The last Formie form the user interacted with, for the send-time repair
let $lastForm = null

// Final line of defence: the blanking can happen between the submit click and
// Formie building its request (it waits on the async Turnstile token first),
// so repair the outgoing FormData itself at the moment of dispatch.
function patchXhrSend() {
	const origSend = XMLHttpRequest.prototype.send

	XMLHttpRequest.prototype.send = function (body) {
		try {
			if (body instanceof FormData && body.has('action') && !body.get('action') && body.has('handle')) {
				const $form = $lastForm || document.querySelector('form[data-form-handle]')

				body.set('action', FORMIE_ACTION)

				if (!body.get('handle') && $form) {
					body.set('handle', $form.getAttribute('data-form-handle'))
				}

				if (body.has('submitAction') && !body.get('submitAction')) {
					body.set('submitAction', 'submit')
				}

				// Servd's CSRF injector keeps the fetched token on window
				const tokenName = csrfTokenName()
				if (body.has(tokenName) && !body.get(tokenName) && window.csrfTokenValue) {
					body.set(tokenName, window.csrfTokenValue)
				}

				console.warn('[formie-guard] repaired outgoing FormData at send time')
			}
		} catch (e) {
			// Never let the guard break a submission
		}

		return origSend.call(this, body)
	}
}

export default function formieHiddenInputGuard() {
	patchXhrSend()

	// The moments a suspended tab comes back to life
	window.addEventListener('pageshow', () => repairAll('pageshow'))
	window.addEventListener('focus', () => repairAll('focus'))
	document.addEventListener('visibilitychange', () => {
		if (!document.hidden) {
			repairAll('visible')
		}
	})

	// The moment of submission. Capture phase, so this runs before Formie's
	// own click/submit handlers build the request.
	document.addEventListener(
		'click',
		(event) => {
			const $target = event.target.closest && event.target.closest('form[data-form-handle]')
			if ($target) {
				$lastForm = $target
			}
			const $btn = event.target.closest && event.target.closest('form[data-form-handle] [type="submit"]')
			if ($btn) {
				repairForm($btn.closest('form[data-form-handle]'), 'submit-click')
			}
		},
		true,
	)
	document.addEventListener(
		'submit',
		(event) => {
			const $form = event.target.closest && event.target.closest('form[data-form-handle]')
			if ($form) {
				repairForm($form, 'submit')
			}
		},
		true,
	)

	// Fallbacks: shortly after load, and on any focus moving into the page
	setTimeout(() => repairAll('initial'), 1500)
	document.addEventListener('focusin', () => repairAll('focusin'))
}
